import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NotebookPen } from "lucide-react";
import { Textarea } from "@akuna/ui";
import { useToast } from "@/hooks/use-toast";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { useUpdatePetReservationFields } from "../useUpdatePetReservationFields";
import { getPetStayNotesLabelKey } from "../petStayNotesLabel";
import { PetProfileSectionEmpty, PetProfileSectionShell } from "../PetProfileSectionShell";

export type PetStayNotesSectionProps = {
  petId: string;
  notes: string | null;
  compact?: boolean;
  showEmpty?: boolean;
};

export function PetStayNotesSection({ petId, notes, compact, showEmpty }: PetStayNotesSectionProps) {
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const { mode } = useDaycareHotelAccess();
  const updateNotes = useUpdatePetReservationFields();
  const [isEditing, setIsEditing] = useState(false);

  const current = notes ?? "";

  if (compact && !showEmpty && !isEditing && !current) return null;

  return (
    <PetProfileSectionShell title={t(getPetStayNotesLabelKey(mode))} icon={NotebookPen} tone="orange" fullWidth compact={compact && !isEditing} isEditing={isEditing} onToggleEdit={() => setIsEditing((current) => !current)} editLabel={t("petProfile.editNotes")} doneLabel={t("petProfile.doneNotes")}>
      {isEditing ? <Textarea
        rows={3}
        aria-label={t(getPetStayNotesLabelKey(mode))}
        className="resize-none mt-2"
        defaultValue={current}
        placeholder={t("petProfile.notesPlaceholder")}
        onBlur={async (event) => {
          const value = event.target.value;
          if (value === current) return;
          try {
            await updateNotes.mutateAsync({ petId, daycareHotelNotes: value || null });
          } catch {
            toast({ title: t("petProfile.saveError"), variant: "destructive" });
          }
        }}
      /> : current ? <p className="whitespace-pre-wrap text-sm text-foreground">{current}</p> : <PetProfileSectionEmpty>{t("petProfile.notesEmpty")}</PetProfileSectionEmpty>}
    </PetProfileSectionShell>
  );
}
